// src/layouts/ProtectedLayout.js
import React, { useContext, useEffect, useState } from "react";
import { Outlet, Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { ThemeContext } from "../context/ThemeContext";
import usePresence from "../hooks/usePresence";
import { usePendingInviteWatcher } from "../hooks/usePendingInviteWatcher";

const ProtectedLayout = () => {
  const { theme } = useContext(ThemeContext);
  const [user, setUser]                 = useState(undefined); // undefined = resolving
  const [profileReady, setProfileReady] = useState(undefined); // undefined = checking

  // Step 1 — resolve auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) setProfileReady(null); // logged out — not relevant
    });
    return unsub;
  }, []);

  // Step 2 — once we have a user, check if profile is set up
  useEffect(() => {
    if (!user) return;
    setProfileReady(undefined);
    getDoc(doc(db, "profiles", user.uid))
      .then((snap) => {
        const hasName = snap.exists() && !!snap.data().displayName?.trim();
        setProfileReady(hasName);
      })
      .catch(() => setProfileReady(true)); // on Firestore error, don't block
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Still resolving auth or profile check
  if (user === undefined || (user && profileReady === undefined)) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        height: "100vh",
        background: "linear-gradient(135deg, #5b4633 0%, #6a523d 50%, #3a2f22 100%)",
      }}>
        <style>{`@keyframes pl-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{
          width: 28, height: 28, borderRadius: "50%",
          border: "3px solid rgba(244,213,141,0.2)",
          borderTopColor: "#f4d58d",
          animation: "pl-spin 0.8s linear infinite",
        }} />
      </div>
    );
  }

  // Not logged in
  if (!user) return <Navigate to="/login" replace />;

  // Logged in but profile not set up → force onboarding
  if (profileReady === false) return <Navigate to="/setup" replace />;

  return <PresenceAndLayout theme={theme} />;
};

// Separated so usePresence only mounts for verified, authenticated users
function PresenceAndLayout({ theme }) {
  usePresence();
  usePendingInviteWatcher();

  return (
    <div
      className="layout-root min-h-screen transition-colors duration-500"
      data-theme={theme}
      style={{
        backgroundColor: "var(--bg)",
        color: "var(--text)",
        position: "relative",
        zIndex: 0,
      }}
    >
      <Outlet />
    </div>
  );
}

export default ProtectedLayout;
